<script setup lang="ts">
import { computed, onMounted, useTemplateRef, watch } from 'vue';
import api from '@/api';
import {
  PRODUCT_LOGO_ASSET,
  PRODUCT_LOGO_STYLE,
  PRODUCT_NAME,
} from '@/constants';
import AppShellHeader from '@/components/layouts/app-shell/app-shell-header.vue';
import HelpDropdown from '@/components/menu/help-dropdown.vue';
import NavigationDropdown from '@/components/menu/navigation-dropdown.vue';
import SettingsDropdown from '@/components/menu/settings-dropdown.vue';
import UserMenuDropdown from '@/components/menu/user-menu-dropdown.vue';
import ConfirmationModal from '@/components/modals/confirmation-modal.vue';
import { provideLayoutScrollContainer } from '@/utils/composables/useLayoutScrollContainer';
import { provideLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useUserSession } from '@/utils/user';
import { userSession } from '@/utils/userSession';
import { useStore } from '@/utils/vue';

/**
 * エージェントに依存しない画面（アカウント・Aconnectの設定）のレイアウト。
 * ヘッダーの見出しと左のメニューだけが違うので、それ以外はここに置く。
 */
defineProps<{
  subtitle: string;
}>();

// 中に表示する既存の画面は、新レイアウトの中で描画される前提で作られている
provideLayoutVariant('next');
const contentRef = useTemplateRef('contentRef');
provideLayoutScrollContainer(contentRef);

// 情報収集のシェルを通らないため、セッションの確認とアクセスの記録はこのシェルで行う
const { userInfo } = useUserSession();

const stopUserInfoWatch = watch(userInfo, async u => {
  if (!u) return;

  await api.trackAccess();
  stopUserInfoWatch();
});

onMounted(() => {
  document.addEventListener('visibilitychange', () => {
    if (!userSession.isActive()) return;
    if (document.visibilityState === 'visible') {
      api.trackAccess();
    }
  });
});

// 既存の画面は情報収集のシェルが出していた確認ダイアログを使うため、このシェルでも出す
const store = useStore();
const confirmationModalOpen = computed(
  () => store.state.confirmationModal.confirmationModalOpen,
);
</script>

<template>
  <div class="aconnect-shell-layout">
    <div class="layout-header">
      <AppShellHeader>
        <template #brand>
          <NavigationDropdown />
          <div class="brand">
            <img
              class="brand-logo"
              :src="PRODUCT_LOGO_ASSET"
              :style="PRODUCT_LOGO_STYLE"
              :alt="PRODUCT_NAME"
            />
            <div class="c-title-bold--s brand-subtitle">{{ subtitle }}</div>
          </div>
        </template>
        <template #actions>
          <HelpDropdown />
          <SettingsDropdown />
          <UserMenuDropdown />
        </template>
      </AppShellHeader>
    </div>
    <slot name="menu" />
    <main ref="contentRef" class="layout-content">
      <slot />
    </main>
  </div>
  <ConfirmationModal v-if="confirmationModalOpen" />
</template>

<style lang="scss" scoped>
.aconnect-shell-layout {
  --layout-header-height: #{$header-height};
  --layout-sticky-top: 0px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: var(--layout-header-height) minmax(0, 1fr);
  height: 100dvh;
  overflow: hidden;
}

.layout-header {
  grid-column: 1 / -1;
  position: relative;
  z-index: var(--z-header);
}

.layout-content {
  overflow: auto;
}

.brand {
  display: flex;
  align-items: end;
  height: 32px;
  padding-bottom: 7px;
  box-sizing: border-box;
}

.brand-subtitle {
  display: flex;
  align-items: end;
  height: 32px;
  line-height: 1;
  margin-left: 8px;
  box-sizing: border-box;
  flex-shrink: 0;
}
</style>
